import { type FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { difference } from 'lodash-es';

import { type RootState } from '@editor2/reducers/root';
import { type Chapter } from '@editor2/types/chapter';
import ChangesDiff from '../common/ChangesDiff';
import { type PageGroup } from '@editor2/types/pageGroup';
import ElementChangesDiff from './ElementChangesDiff';
import {
	getChapterTitle,
	getEntityById,
	getInitialIndex,
	getPageByElementId,
	getPageGroupTitle,
	getPageTitle,
	getParentEntityByPageId
} from '../helpers';
import { elementHeader, pageHeader } from '../styles';
import ExpandChangesButton from '../common/ExpandChangesButton';
import DeletedItem from '../common/DeletedItem';
import ChildrenChangesWrapper from '../common/ChildrenChangesWrapper';
import { getElementHumanName } from '@editor2/services/elementService';

interface Props {
	courseId: number;
	pageChanges: any;
	index: number;
	initChapter?: Chapter;
	initPageGroup?: PageGroup;
}

const PageChangesDiff: FC<Props> = (props) => {
	const { courseId, pageChanges, index, initChapter, initPageGroup } = props;

	const { id, elements, ...attributeChanges } = pageChanges;

	const { pages, initialCourse, chapters, page_groups, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const [showChildren, setShowChildren] = useState(true);

	const getIndex = (pages, pageId) => {
		return pages ? getInitialIndex(pages, pageId) : -1;
	};

	const initialPage = getEntityById(initialCourse.pages, id);
	const currentPage = getEntityById(pages, id) || pages.entities[attributeChanges.family_id];
	const initialIndex = initChapter
		? getIndex(initChapter?.pages, initialPage?.family_id)
		: getIndex(initPageGroup?.pages, initialPage?.family_id);

	if (Object.keys(pageChanges).length === 1 && index === initialIndex) return <></>;

	const getParentName = () => {
		const initChapter =
			getParentEntityByPageId(initialCourse.chapters, currentPage.family_id) ||
			getParentEntityByPageId(chapters, currentPage.family_id);
		const initPageGroup =
			getParentEntityByPageId(initialCourse.page_groups, currentPage.family_id) ||
			getParentEntityByPageId(page_groups, currentPage.family_id);

		if (initChapter) return getChapterTitle(initChapter);
		if (initPageGroup) return getPageGroupTitle(initPageGroup);
		return '';
	};

	const getChangesElementIds = () => {
		return elements.map(
			(entity) => entity.family_id || getEntityById(initialCourse.elements, entity.id).family_id
		);
	};

	return (
		<>
			<ChangesDiff
				title={getPageTitle(currentPage)}
				index={index}
				initialIndex={initialIndex}
				initial={initialPage}
				attributeChanges={attributeChanges}
				parentName={getParentName()}
				styles={pageHeader}
			/>

			{elements && !showChildren && <ExpandChangesButton onClick={() => setShowChildren(true)} />}

			{showChildren && (
				<ChildrenChangesWrapper setShowChildren={setShowChildren}>
					{elements &&
						elements.map((elementObj, index) => (
							<ElementChangesDiff
								courseId={courseId}
								elementChanges={elementObj}
								index={index}
								initialPage={initialPage}
								key={index}
							/>
						))}

					{elements &&
						difference(initialPage?.elements || [], getChangesElementIds()).map((family_id) => {
							const page = getPageByElementId(pages, family_id);
							const element = initialCourse.elements.entities[family_id];

							return (
								<DeletedItem
									key={family_id}
									title={getElementHumanName(config, page?.page_type, element)}
									movedTo={page && getPageTitle(page)}
									styles={elementHeader}
								/>
							);
						})}
				</ChildrenChangesWrapper>
			)}
		</>
	);
};

export default PageChangesDiff;
